import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { EmailAddressBookService } from '@sbd-doug-frontend/shared/lib/shared-services/email-address-book.service';
import { SharedAdminUsersService } from '@sbd-doug-frontend/shared/lib/shared-services/shared-admin-users.service';
import { User } from '@sbd-doug-frontend/app/core/user/user.types';
import {
  ActivityActor,
  ActivityCallType,
  CreateCallActivityEventLogInput,
  QuoteRequestEventLog,
  RelatedQuoteRequestRef,
} from '@sbd-doug-frontend/shared/lib/types/activity-viewer/activity-event-log.types';
import { EmailContact } from '@sbd-doug-frontend/shared/lib/types/email-viewer/email-compose.types';
import { Observable, forkJoin, map } from 'rxjs';

interface AccountQuoteRequestRow {
  quote_request_id: number;
  order_number?: string;
  user_id?: number;
  company_name?: string;
  bond_amount?: number;
  state_abbreviation?: string;
  surety_bond_label?: string;
}

interface AccountQuoteRequestsResponse {
  success?: boolean;
  data?: {
    account?: {
      quote_requests?: AccountQuoteRequestRow[];
      quoteRequests?: AccountQuoteRequestRow[];
    };
    quote_requests?: AccountQuoteRequestRow[];
    quoteRequests?: AccountQuoteRequestRow[];
  };
}

interface AdminUserLookupItem {
  admin_user_id: number;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountManagerActivityFormService {
  private _httpClient = inject(HttpClient);
  private _addressBookService = inject(EmailAddressBookService);
  private _adminUsersService = inject(SharedAdminUsersService);

  loadActors(accountId: number, currentAdmin: User | null): Observable<ActivityActor[]> {
    return forkJoin({
      contacts: this._addressBookService.fetchContacts(accountId),
      adminUsers: this._adminUsersService.getAdminUsersList(),
    }).pipe(map(({ contacts, adminUsers }) => this._mergeActors(contacts, adminUsers, currentAdmin)));
  }

  searchRelatedRequests(query: string, accountId: number): Observable<RelatedQuoteRequestRef[]> {
    return this._httpClient
      .get<AccountQuoteRequestsResponse>(`${environment.apiBaseUrl}/api/v4/account/${accountId}/quote-requests`, {
        params: this._buildQuoteRequestSearchParams(query),
      })
      .pipe(
        map((response) => this._extractQuoteRequests(response)),
        map((quoteRequests) => this._filterQuoteRequests(quoteRequests, query)),
        map((quoteRequests) => quoteRequests.map((quoteRequest) => this._mapQuoteRequest(quoteRequest)))
      );
  }

  buildCreateActivityEventLogPayload(input: CreateCallActivityEventLogInput): QuoteRequestEventLog {
    const nowIso = new Date().toISOString();
    return {
      event_log_type_id: input.eventTypeId,
      event_log_time: nowIso,
      event_log_admin_id: input.adminUserId,
      event_log_user_id: input.accountUserId,
      event_log_comment: input.subject.trim(),
      event_log_exception: '',
      ctime: nowIso,
      event_log_info: {
        activity: {
          actors: input.actors,
          notes: input.notes,
          related_quote_requests: input.relatedRequests,
        },
      },
      quote_request_id: input.quoteRequestId,
      event_log_source: 'activity-drawer-call-form',
      event_log_account_id: input.accountId,
      event_log_account_user_id: input.accountUserId,
      event_log_lead_id: null,
      is_alias: false,
    };
  }

  getCallTypeLabel(typeId: ActivityCallType): string {
    switch (typeId) {
      case ActivityCallType.TYPE_RECEIVEPHONE:
        return 'Call In';
      case ActivityCallType.TYPE_RECEIVEVOICEMAIL:
        return 'Received Voicemail';
      case ActivityCallType.TYPE_DIALPHONE:
        return 'Call Out';
      case ActivityCallType.TYPE_LEAVEVOICEMAIL:
        return 'Left Voicemail';
      case ActivityCallType.TYPE_NOVOICEMAILAVAILABLE:
        return 'No Voicemail Available';
      default:
        return 'Call Activity';
    }
  }

  private _mergeActors(
    contacts: EmailContact[],
    adminUsers: AdminUserLookupItem[],
    currentAdmin: User | null
  ): ActivityActor[] {
    const accountUserActors = contacts
      .filter((contact) => contact.contact_type === 'account_user' && contact.account_user_id)
      .map((contact) => {
        const firstName = contact.contact_first_name || '';
        const lastName = contact.contact_last_name || '';
        return {
          actor_id: contact.account_user_id as number,
          actor_scope_key: 'cipxaccountuser',
          actor_full_name: `${firstName} ${lastName}`.trim() || contact.display_name || 'Unknown User',
          actor_first_name: firstName,
          actor_last_name: lastName,
          actor_initials: this._buildInitials(firstName, lastName),
        } as ActivityActor;
      });

    const adminActors = adminUsers.map((adminUser) => this._mapAdminActor(adminUser));
    const adminActor = this._mapCurrentAdminActor(currentAdmin);

    const merged = adminActor
      ? [...accountUserActors, ...adminActors, adminActor]
      : [...accountUserActors, ...adminActors];
    const seen = new Set<string>();
    return merged.filter((actor) => {
      const key = `${actor.actor_scope_key}:${actor.actor_id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private _mapAdminActor(adminUser: AdminUserLookupItem): ActivityActor {
    const firstName = adminUser.user_first_name || '';
    const lastName = adminUser.user_last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || adminUser.user_email || 'Admin User';

    return {
      actor_id: adminUser.admin_user_id,
      actor_scope_key: 'xbadmin',
      actor_full_name: fullName,
      actor_first_name: firstName,
      actor_last_name: lastName,
      actor_initials: this._buildInitials(firstName, lastName),
    };
  }

  private _mapCurrentAdminActor(currentAdmin: User | null): ActivityActor | null {
    if (!currentAdmin?.id) {
      return null;
    }

    const nameParts = (currentAdmin.name ?? '').trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ');
    const fullName = currentAdmin.name?.trim() || currentAdmin.email || 'Current Admin';

    return {
      actor_id: Number(currentAdmin.id),
      actor_scope_key: 'xbadmin',
      actor_full_name: fullName,
      actor_first_name: firstName,
      actor_last_name: lastName,
      actor_initials: currentAdmin.initials || this._buildInitials(firstName, lastName),
    };
  }

  private _extractQuoteRequests(response: AccountQuoteRequestsResponse): AccountQuoteRequestRow[] {
    return (
      response.data?.account?.quote_requests ??
      response.data?.account?.quoteRequests ??
      response.data?.quote_requests ??
      response.data?.quoteRequests ??
      []
    );
  }

  private _buildQuoteRequestSearchParams(query: string): Record<string, string | number> {
    const normalizedQuery = query.trim();
    const params: Record<string, string | number> = {
      limit: 50,
      pageIndex: 0,
      offset: 0,
    };

    if (!normalizedQuery) {
      return params;
    }

    if (/^\d+$/.test(normalizedQuery)) {
      params['quote_request_id[eq]'] = normalizedQuery;
      return params;
    }

    if (/^w/i.test(normalizedQuery)) {
      params['order_number[lstart]'] = normalizedQuery;
      return params;
    }

    params['company_name[l]'] = normalizedQuery;
    return params;
  }

  private _filterQuoteRequests(quoteRequests: AccountQuoteRequestRow[], query: string): AccountQuoteRequestRow[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return quoteRequests;
    }

    return quoteRequests.filter((quoteRequest) => {
      const searchable = [
        quoteRequest.quote_request_id,
        quoteRequest.order_number,
        quoteRequest.company_name,
        quoteRequest.state_abbreviation,
        quoteRequest.surety_bond_label,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase();

      return searchable.includes(normalizedQuery);
    });
  }

  private _mapQuoteRequest(quoteRequest: AccountQuoteRequestRow): RelatedQuoteRequestRef {
    return {
      quote_request_id: quoteRequest.quote_request_id,
      order_number: quoteRequest.order_number,
      label: this._buildQuoteRequestLabel(quoteRequest),
      user_id: quoteRequest.user_id,
      company_name: quoteRequest.company_name,
      bond_amount: quoteRequest.bond_amount,
      state_abbreviation: quoteRequest.state_abbreviation,
      surety_bond_label: quoteRequest.surety_bond_label,
    };
  }

  private _buildQuoteRequestLabel(quoteRequest: AccountQuoteRequestRow): string {
    const parts = [
      quoteRequest.order_number,
      quoteRequest.company_name,
      quoteRequest.bond_amount ? `$${quoteRequest.bond_amount}` : null,
      quoteRequest.state_abbreviation,
      quoteRequest.surety_bond_label,
    ].filter(Boolean);

    return parts.join(' - ') || `Quote Request ${quoteRequest.quote_request_id}`;
  }

  private _buildInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
}
